// @flow
import type { Customer, IdentificationType } from '@tripica/model-types';
import { formats } from 'mrz';
import { getTime, getYear, parse, subYears } from 'date-fns';
import type { Identification } from '../modules/SignUp/Identifications/reducer';
import type { MrzResult } from '../scenes/SignUp/PassportCheck/lib';

export const IDENTIFICATION_TYPE: { [IdentificationType]: IdentificationType } = {
  IDENTITY_CARD: 'IDENTITY_CARD',
  PASSPORT: 'PASSPORT',
};

const defaultResultFormatter: FormattedMrzResults = {
  customer: {
    gender: 'FEMALE',
    fullName: '',
    givenName: '',
    familyName: '',
    birthdate: null,
    nationality: '',
    billingAddress: {
      street1: '',
      postCode: '',
      stateOrProvince: '',
      city: '',
    },
  },
  identification: {
    identificationType: IDENTIFICATION_TYPE.IDENTITY_CARD,
    identificationId: '',
    contentMediaType: 'image/jpg',
    contentBase64: 'base64image',
    characteristics: {
      status: 'VALID',
      idvPartner: 'TRPC',
      idvResult: 'clear',
    },
  },
};

const formatDocumentId = (results: MrzResult): string => {
  let documentNumberFormated = results.fields.documentNumber ? results.fields.documentNumber.replace(/[<>]/g, '') : '';

  if (results.format === formats.FRENCH_NATIONAL_ID) {
    const { issueDate, administrativeCode2, documentNumber } = results.fields;
    documentNumberFormated = issueDate + administrativeCode2 + documentNumber;
  }

  return documentNumberFormated;
};

export const getIdentificationType = (format: string): IdentificationType => {
  switch (format) {
    case formats.TD1:
    case formats.TD2:
    case formats.FRENCH_NATIONAL_ID:
      return IDENTIFICATION_TYPE.IDENTITY_CARD;
    case formats.TD3:
      return IDENTIFICATION_TYPE.PASSPORT;
    default:
      throw new Error('Unsupported identification card');
  }
};

export const formatName = (name: string): string => {
  return name ? name.toLowerCase().replace(/\b\w/g, firstLetter => firstLetter.toUpperCase()) : '';
};

export default (results: MrzResult | null, image: string): FormattedMrzResults => {
  if (!results) {
    return defaultResultFormatter;
  }

  const gender = results.fields.sex === 'male' ? 'MALE' : 'FEMALE';

  const birthdateDate = results.fields.birthDate ? parse(results.fields.birthDate, 'yyMMdd', new Date()) : null;

  // Passports cards have a year in the format YY. So for 58, we don't know if it is 2058 or 1958
  // arbitrarily, if the year is more than the current year, we set it to a century earlier
  const birthdateYear = birthdateDate
    ? (getYear(birthdateDate) > new Date().getFullYear() ? subYears(birthdateDate, 100) : birthdateDate)
    : null;

  const id = formatDocumentId(results);

  const givenName = formatName(results.fields.firstName.split(' ')[0] || '');
  const familyName = formatName(results.fields.lastName || '');

  const identificationType = getIdentificationType(results.format);

  return {
    customer: {
      gender,
      givenName,
      familyName,
      fullName: (givenName + ' ' + familyName).trim(),
      birthdate: getTime(birthdateYear) || null,
      nationality: results.fields.issuingState || '',
      billingAddress: {
        street1: '',
        postCode: '',
        city: '',
        stateOrProvince: '',
      },
    },
    identification: {
      identificationId: id,
      identificationType,
      contentBase64: image,
      contentMediaType: 'image/jpg',
      characteristics: {
        status: 'VALID',
        idvPartner: 'TRPC',
        idvResult: 'clear',
      },
    },
  };
};

export type FormattedMrzResults = {
  customer: $Shape<Customer>,
  identification: Identification,
};
